import {
  formatNumber,
  Chart,
  formatDatetime,
  CurrencyValue,
  ChartSkeleton,
  assetNameToRgba
} from '@blockanalitica/ui';
import { useMemo, useCallback, useState } from 'react';
import { useApi } from '@/api';
import type {
  ChartConfig,
  ChartDataset
} from '@blockanalitica/ui/package/types/src/components/charts/Chart';
import type { SimpleChartData } from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import _ from 'lodash';
import { ChartTimeFilter } from '@/components';

export default function HistoricCollateralsGraphs({
  marketAddress
}: {
  marketAddress: string;
}) {
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [data, error] = useApi<SimpleChartData[]>({
    url: `/markets/${marketAddress}/liquidations/collateral-bought/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),

    [chartDaysAgo]
  );

  const datasets: ChartDataset[] = useMemo(() => {
    if (!data) return [];
    const series: ChartDataset[] = [];

    const grouped = _.groupBy(data, 'underlying_symbol');

    Object.entries(grouped).map(([key, rows]) => {
      series.push({
        label: key,
        borderColor: assetNameToRgba(key) || '#fff',
        backgroundColor: assetNameToRgba(key) || '#fff',
        data: rows.map((row) => ({
          x: row.date,
          y: row.base_amount
        }))
      });
    });

    return series;
  }, [data]);

  const config: ChartConfig = useMemo(() => {
    return {
      type: 'line',
      options: {
        scales: {
          x: {
            stacked: true
          },
          y: {
            stacked: true
          }
        },
        fill: true
      }
    };
  }, []);

  const yScaleValueFormatter = useCallback(
    (value: string | number) => `${formatNumber(Number(value))}`,
    []
  );

  const valueFormatter = (value: string | number) => (
    <CurrencyValue
      value={value}
      options={{
        currency: 'USD'
      }}
    />
  );

  if (error) {
    throw error;
  }

  if (!data) {
    return <ChartSkeleton />;
  }
  return (
    <Chart
      chartDatasets={datasets}
      valueFormatter={valueFormatter}
      yScaleValueFormatter={yScaleValueFormatter}
      config={config}
      timeFilter={timeFilter}
      tooltip={{
        labelFormatter: (value) => `${value}`,
        titleFormatter: (_value, tootlipItems) =>
          // @ts-ignore
          `${formatDatetime(tootlipItems[0].raw.x, { format: 'MMM dd' })}`,
        valueFormatter: (value) => `${formatNumber(Number(value))}`
      }}
      showKpisAsTooltip
    />
  );
}
